import { useEffect, useCallback, useRef } from 'react';
import { useAppContext } from '@/context/AppContext';
import { Link, Action } from '@/types/core/Link';
import { Item } from '@/types/core/Item';

/**
 * Custom hook for managing and evaluating property animations based on different link types.
 * 
 * The linking system allows you to:
 * 1. Create beat-synchronized property animations
 * 2. Create video-based property animations
 * 3. Create audio-reactive property animations
 * 4. Target multiple properties on multiple items simultaneously
 * 5. Apply various easing and curve types to the animations
 */
export const useLinking = () => {
  const { state, updateItem, clock } = useAppContext();
  
  // Track active animations with a ref to avoid re-renders
  const activeAnimations = useRef(new Map<string, {
    startTime: number,
    isActive: boolean
  }>());

  /**
   * Checks if a link is currently active based on its type.
   */
  const isLinkActive = useCallback((link: Link): boolean => {
    if (!link.properties.enabled) return false;
    
    switch (link.linkType) {
      case 'beat':
        // Check if we're on a trigger beat
        if (!clock) return false;
        const beatInfo = clock.getBeatInfo();
        return link.settings.triggerBeats?.includes(beatInfo.beat) || false;
        
      case 'video':
        // Placeholder implementation - would connect to video processing system
        return false;
        
      case 'audio':
        // Placeholder implementation - would connect to audio analysis system
        return false;
        
      default:
        return false;
    }
  }, [clock]);

  /**
   * Applies easing to a progress value for more natural animations.
   */
  const applyEasing = useCallback((progress: number, easing: string): number => {
    switch (easing) {
      case 'easeIn':
        return progress * progress;
      case 'easeOut':
        return 1 - (1 - progress) * (1 - progress);
      case 'easeInOut':
        return progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      case 'linear':
      default:
        return progress;
    }
  }, []);

  /**
   * Applies a curve to the progress value.
   */
  const applyCurve = useCallback((progress: number, curve: string): number => {
    switch (curve) {
      case 'smooth':
        return (1 - Math.cos(progress * Math.PI)) / 2;
      case 'linear':
      default:
        return progress;
    }
  }, []);

  /**
   * Calculates the current value for an action based on its type and progress.
   */
  const calculateActionValue = useCallback((
    action: Action,
    progress: number
  ): any => {
    // Apply curve and easing
    let finalProgress = applyCurve(progress, action.curve);
    finalProgress = applyEasing(finalProgress, action.easing);
    
    if (action.type === 'minmax' && action.range) {
      // Use rest value when not active and it's defined
      if (action.restValue !== undefined && progress === 0) {
        return action.restValue;
      }
      
      return action.range.min + (action.range.max - action.range.min) * finalProgress;
    } 
    else if (action.type === 'toggle' && action.defaultState !== undefined) {
      // Use rest value when not active and it's defined
      if (action.restValue !== undefined && progress === 0) {
        return action.restValue;
      }
      
      return progress > 0.5 ? !action.defaultState : action.defaultState;
    }
    
    return undefined;
  }, [applyEasing, applyCurve]);

  /**
   * Evaluates a single action within a link.
   */
  const evaluateAction = useCallback((
    link: Link,
    action: Action,
    isLinkActive: boolean,
    world: { [key: string]: Item }
  ) => {
    if (!action.enabled || !action.targetItemId || !action.targetProperty) return;

    const targetItem = world[action.targetItemId];
    if (!targetItem) return;

    // Generate unique action ID
    const actionId = `${link.id}-${action.id}`;
    
    // Get current animation state or create a new one
    let animState = activeAnimations.current.get(actionId);
    if (!animState) {
      animState = { startTime: 0, isActive: false };
      activeAnimations.current.set(actionId, animState);
    }
    
    // Get the current time
    const currentTime = clock?.getElapsedTime() || 0;
    
    // Get the duration in beats (default to 1 if not specified)
    const duration = link.linkType === 'beat' ? link.settings.duration || 1 : 1;
    
    // If the link just became active and the action wasn't active before, start a new animation
    if (isLinkActive && !animState.isActive) {
      animState.startTime = currentTime;
      animState.isActive = true;
    }
    
    // Calculate how many beats have passed since the animation started
    const beatsSinceStart = ((currentTime - animState.startTime) * (clock?.getBpm() || 120)) / 60;
    
    // Check if the animation should still be running based on duration
    const isAnimationRunning = beatsSinceStart < duration;
    
    // If the animation has completed, mark it as inactive
    if (!isAnimationRunning && animState.isActive) {
      animState.isActive = false;
      
      // If there's a rest value, apply it when the animation completes
      if (action.restValue !== undefined) {
        // Apply the rest value directly
        const updatedProperties = getUpdatedProperties(targetItem, action.targetProperty, action.restValue);
        updateItem(targetItem.id, { properties: updatedProperties });
      }
      return;
    }
    
    // Only calculate and apply progress if the animation is running
    if (isAnimationRunning && animState.isActive) {
      // Calculate progress as a value between 0 and 1
      const progress = Math.min(beatsSinceStart / duration, 1);
      
      // Calculate the new value
      const value = calculateActionValue(action, progress);
      if (value !== undefined) {
        // Apply the value directly
        const updatedProperties = getUpdatedProperties(targetItem, action.targetProperty, value);
        updateItem(targetItem.id, { properties: updatedProperties });
      }
    }
  }, [clock, calculateActionValue, updateItem]);

  /**
   * Helper function to create updated properties object with the new value
   */
  const getUpdatedProperties = useCallback((
    targetItem: Item, 
    propertyPath: string, 
    value: any,
    currentProperties?: Record<string, any>
  ): Record<string, any> => {
    const pathParts = propertyPath.split('.');
    const result = currentProperties || { ...targetItem.properties };
    
    // Handle simple case (top-level property)
    if (pathParts.length === 1) {
      result[pathParts[0]] = value;
      return result;
    }
    
    // Handle nested properties
    let current = result;
    for (let i = 0; i < pathParts.length - 1; i++) {
      const part = pathParts[i];
      current[part] = { ...current[part] };
      current = current[part];
    }
    
    // Set the final value
    current[pathParts[pathParts.length - 1]] = value;
    return result;
  }, []);

  /**
   * Evaluates a single link, processing all its actions.
   */
  const evaluateLink = useCallback((link: Link) => {
    if (!link.properties.enabled) return;

    const world = state.worlds[state.activeWorldId || ''];
    if (!world) return;

    // Check if the link is active based on its type
    const isActive = isLinkActive(link);

    // Create a map to collect all property changes for each target item
    const itemChanges = new Map<string, Record<string, any>>();

    // Process each enabled action and collect changes
    link.actions.forEach((action: Action) => {
      if (!action.enabled || !action.targetItemId || !action.targetProperty) return;

      const targetItem = world.items[action.targetItemId];
      if (!targetItem) return;

      // Generate unique action ID
      const actionId = `${link.id}-${action.id}`;
      
      // Get current animation state or create a new one
      let animState = activeAnimations.current.get(actionId);
      if (!animState) {
        animState = { startTime: 0, isActive: false };
        activeAnimations.current.set(actionId, animState);
      }
      
      // Get the current time
      const currentTime = clock?.getElapsedTime() || 0;
      
      // Get the duration in beats (default to 1 if not specified)
      const duration = link.linkType === 'beat' ? link.settings.duration || 1 : 1;
      
      // If the link just became active and the action wasn't active before, start a new animation
      if (isActive && !animState.isActive) {
        animState.startTime = currentTime;
        animState.isActive = true;
      }
      
      // Calculate how many beats have passed since the animation started
      const beatsSinceStart = ((currentTime - animState.startTime) * (clock?.getBpm() || 120)) / 60;
      
      // Check if the animation should still be running based on duration
      const isAnimationRunning = beatsSinceStart < duration;
      
      // If the animation has completed, mark it as inactive
      if (!isAnimationRunning && animState.isActive) {
        animState.isActive = false;
        
        // If there's a rest value, collect it for application when the animation completes
        if (action.restValue !== undefined) {
          // Get the current changes for this target item or create a new object
          const currentChanges = itemChanges.get(targetItem.id) || { ...targetItem.properties };
          
          // Update the property with the rest value
          const updatedProperties = getUpdatedProperties(
            targetItem,
            action.targetProperty, 
            action.restValue,
            currentChanges
          );
          
          // Store the updated properties
          itemChanges.set(targetItem.id, updatedProperties);
        }
        return;
      }
      
      // Only calculate and apply progress if the animation is running
      if (isAnimationRunning && animState.isActive) {
        // Calculate progress as a value between 0 and 1
        const progress = Math.min(beatsSinceStart / duration, 1);
        
        // Calculate the new value
        const value = calculateActionValue(action, progress);
        if (value !== undefined) {
          // Get the current changes for this target item or create a new object
          const currentChanges = itemChanges.get(targetItem.id) || { ...targetItem.properties };
          
          // Update the property with the new value
          const updatedProperties = getUpdatedProperties(
            targetItem,
            action.targetProperty, 
            value,
            currentChanges
          );
          
          // Store the updated properties
          itemChanges.set(targetItem.id, updatedProperties);
        }
      }
    });

    // Apply all collected changes to the target items
    itemChanges.forEach((properties, itemId) => {
      updateItem(itemId, { properties });
    });
  }, [state.worlds, state.activeWorldId, isLinkActive, calculateActionValue, getUpdatedProperties, updateItem, clock]);

  // Set up animation frame handler for smooth updates
  useEffect(() => {
    if (!clock) return;

    let animationFrameId: number;

    const animate = () => {
      const world = state.worlds[state.activeWorldId || ''];
      if (world) {
        // Only evaluate links that are enabled
        Object.values(world.items)
          .filter((item): item is Link => item.type === 'link' && item.properties.enabled === true)
          .forEach(evaluateLink);
      }
      
      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [clock, state.worlds, state.activeWorldId, evaluateLink]);

  return {
    /**
     * Manually triggers evaluation of all links in the current world.
     * Normally not needed as links are automatically evaluated each frame.
     */
    evaluateLinks: useCallback(() => {
      const world = state.worlds[state.activeWorldId || ''];
      if (world) {
        Object.values(world.items)
          .filter((item): item is Link => item.type === 'link')
          .forEach(evaluateLink);
      }
    }, [state.worlds, state.activeWorldId, evaluateLink])
  };
}; 